import { cv, type CvExperience, type CvLink } from '../../cv';
import chart from './chart_ready.json';

type RawProject = {
  id: string;
  category: 'professional' | 'personal';
  display_name: string;
  sort_order: number;
  parent: string | null;
  points: [number, number, number][]; // [epoch_s, value_log, weight_linear]
};

export type EntityKind = 'company' | 'client';

export type PulseEntity = {
  id: string;
  slug: string;
  displayName: string;
  kind: EntityKind;
  parent: string | null;
  role?: string;
  period: string;
  meta?: string;
  orgHref?: string;
  paragraphs: string[];
  links: CvLink[];
  timeFrom: number;
  timeTo: number;
  timeCenter: number;
  weight: number;
};

const projects = chart.projects as unknown as RawProject[];
const START = chart.start ?? 0;
const END = chart.end ?? 1;
const SPAN = Math.max(1, END - START);

const CLIENT_COPY: Record<string, { blurb?: string; href?: string }> = {
  Klarity: { blurb: 'A client engagement delivered through Kilta.' },
  'Yoga Garden': {},
  'The Editors': {},
  'Kamnářství Třeboň': {},
  'Artwork Framer': {},
  XTND: { blurb: 'A client engagement delivered through Kilta.' },
};

const slugify = (name: string) =>
  name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .toLowerCase();

const yr = (epochSeconds: number) =>
  new Date(epochSeconds * 1000).getUTCFullYear();

const cvByOrg = new Map<string, CvExperience>(
  cv.experience.map((e) => [e.org, e])
);

function build(p: RawProject): PulseEntity {
  const experience = cvByOrg.get(p.id);
  const kind: EntityKind = experience ? 'company' : 'client';
  const client = CLIENT_COPY[p.id];

  let first = Infinity;
  let last = -Infinity;
  let wsum = 0;
  let wfsum = 0;
  for (const [t, , w] of p.points) {
    if (t < first) first = t;
    if (t > last) last = t;
    const f = (t - START) / SPAN;
    wsum += w;
    wfsum += w * f;
  }
  const timeFrom = Number.isFinite(first) ? (first - START) / SPAN : 0;
  const timeTo = Number.isFinite(last) ? (last - START) / SPAN : 1;
  const timeCenter = wsum > 0 ? wfsum / wsum : (timeFrom + timeTo) / 2;

  const firstYr = yr(Number.isFinite(first) ? first : START);
  const lastYr = yr(Number.isFinite(last) ? last : END);
  const derivedPeriod = firstYr === lastYr ? `${firstYr}` : `${firstYr}–${lastYr}`;

  const paragraphs = experience
    ? experience.paragraphs
    : client?.blurb
      ? [client.blurb]
      : [];

  return {
    id: p.id,
    slug: slugify(p.display_name),
    displayName: p.display_name,
    kind,
    parent: p.parent ?? null,
    role: experience?.role,
    period: experience?.period ?? derivedPeriod,
    meta: experience?.meta,
    orgHref: experience?.orgHref ?? client?.href,
    paragraphs,
    links: [],
    timeFrom,
    timeTo,
    timeCenter,
    weight: wsum,
  };
}

export const entities: PulseEntity[] = projects
  .filter((p) => p.category === 'professional')
  .sort((a, b) => a.sort_order - b.sort_order)
  .map(build);

export const timeline: PulseEntity[] = [...entities].sort(
  (a, b) => b.timeTo - a.timeTo || b.weight - a.weight
);

export const getEntityBySlug = (slug: string): PulseEntity | undefined =>
  entities.find((e) => e.slug === slug);

export const entitySlugs = entities.map((e) => e.slug);
